// Discord API constants
const DISCORD_API_URL = 'https://discord.com/api/v10';
const CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID || '1356999787659919503';
const CLIENT_SECRET = process.env.REACT_APP_DISCORD_CLIENT_SECRET || 'DydreMXwxezeY9P2XDv1mMskjVrO3v_b';
const BOT_TOKEN = process.env.REACT_APP_DISCORD_BOT_TOKEN || 'MTM1Njk5OTc4NzY1OTkxOTUwMw.GuJ9K0.5Vd3O6iQU172qrZFDt82nW21kYCkIu-EKfLnAQ';
const REDIRECT_URI = process.env.REACT_APP_DISCORD_REDIRECT_URI || 'http://localhost:3000/discord-callback';
const GUILD_ID = process.env.REACT_APP_DISCORD_GUILD_ID || '1037517581063553104'; // Palm Heist server ID

// Log environment variables to debug (remove in production)
console.log('Discord Client ID:', CLIENT_ID);
console.log('Discord Guild ID:', GUILD_ID);

// Bot permissions integer: 379968
// This includes the permissions you selected in the Discord Developer Portal

// Palm Heist specific channel IDs - you'll need to replace these with actual channel IDs
const PALM_HEIST_CHANNELS = {
  GENERAL: 'general_channel_id',
  ANNOUNCEMENTS: 'announcements_channel_id',
  TRADING: 'trading_channel_id',
  HELP: 'help_channel_id'
};

class DiscordService {
  constructor() {
    this.accessToken = localStorage.getItem('discord_access_token') || null;
    this.refreshToken = localStorage.getItem('discord_refresh_token') || null;
    this.tokenExpiry = localStorage.getItem('discord_token_expiry') || null;
    this.user = JSON.parse(localStorage.getItem('discord_user') || 'null');
    this.guildMember = JSON.parse(localStorage.getItem('discord_guild_member') || 'null');
  }

  // Check if user is connected to Discord
  isConnected() {
    return !!this.accessToken && !!this.user;
  }

  // Check if user is a member of the Palm Heist server
  isGuildMember() {
    return !!this.guildMember;
  }

  // Generate OAuth2 authorization URL with Palm Heist specific scopes
  getAuthUrl() {
    const scope = 'identify email guilds guilds.join messages.read';
    return `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(scope)}&permissions=379968`;
  }
  
  // Generate bot invite URL
  getBotInviteUrl() {
    return `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&permissions=379968&scope=bot%20applications.commands`;
  }

  // Exchange authorization code for access token
  async getToken(code) {
    try {
      const formData = new URLSearchParams();
      formData.append('client_id', CLIENT_ID);
      formData.append('client_secret', CLIENT_SECRET);
      formData.append('grant_type', 'authorization_code');
      formData.append('code', code);
      formData.append('redirect_uri', REDIRECT_URI);

      const response = await fetch(`${DISCORD_API_URL}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      const data = await response.json();

      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiry = Date.now() + data.expires_in * 1000;

      localStorage.setItem('discord_access_token', this.accessToken);
      localStorage.setItem('discord_refresh_token', this.refreshToken);
      localStorage.setItem('discord_token_expiry', this.tokenExpiry);

      await this.fetchUserDetails();
      await this.joinGuild();
      await this.fetchGuildMember();
      
      return true;
    } catch (error) {
      console.error('Error getting Discord token:', error);
      this._handleApiError(error, 'Failed to authenticate with Discord');
      return false;
    }
  }

  // Refresh access token when expired
  async refreshAccessToken() {
    if (!this.refreshToken) return false;

    try {
      const formData = new URLSearchParams();
      formData.append('client_id', CLIENT_ID);
      formData.append('client_secret', CLIENT_SECRET);
      formData.append('grant_type', 'refresh_token');
      formData.append('refresh_token', this.refreshToken);

      const response = await fetch(`${DISCORD_API_URL}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      const data = await response.json();

      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiry = Date.now() + data.expires_in * 1000;

      localStorage.setItem('discord_access_token', this.accessToken);
      localStorage.setItem('discord_refresh_token', this.refreshToken);
      localStorage.setItem('discord_token_expiry', this.tokenExpiry);

      return true;
    } catch (error) {
      console.error('Error refreshing Discord token:', error);
      this._handleApiError(error, 'Failed to refresh Discord token');
      this.logout();
      return false;
    }
  }

  // Check if token is valid and refresh if needed
  async ensureValidToken() {
    if (!this.accessToken) return false;
    
    if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
      return await this.refreshAccessToken();
    }
    
    return true;
  }

  // Fetch user details
  async fetchUserDetails() {
    if (!await this.ensureValidToken()) return null;

    try {
      const response = await fetch(`${DISCORD_API_URL}/users/@me`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      const data = await response.json();

      this.user = data;
      localStorage.setItem('discord_user', JSON.stringify(this.user));
      return this.user;
    } catch (error) {
      console.error('Error fetching Discord user details:', error);
      this._handleApiError(error, 'Failed to fetch user details');
      return null;
    }
  }

  // Join the Palm Heist server if not already a member
  async joinGuild() {
    if (!await this.ensureValidToken() || !this.user) return false;

    try {
      const response = await fetch(`${DISCORD_API_URL}/guilds/${GUILD_ID}/members/${this.user.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bot ${BOT_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: this.accessToken,
        }),
      });
      
      // 204 No Content is returned if the user is already a member
      if (response.status === 204) {
        return true;
      }
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error joining Discord guild:', error);
      this._handleApiError(error, 'Failed to join Palm Heist Discord server');
      return false;
    }
  }

  // Fetch user's guild member details
  async fetchGuildMember() {
    console.log('Fetching guild member for user:', this.user?.id);
    if (!await this.ensureValidToken() || !this.user) return null;

    try {
      const response = await fetch(`${DISCORD_API_URL}/guilds/${GUILD_ID}/members/${this.user.id}`, {
        headers: {
          Authorization: `Bot ${BOT_TOKEN}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      this.guildMember = data;
      localStorage.setItem('discord_guild_member', JSON.stringify(this.guildMember));
      return this.guildMember;
    } catch (error) {
      console.error('Error fetching guild member:', error);
      this._handleApiError(error, 'Failed to fetch Palm Heist server membership details');
      return null;
    }
  }

  // Fetch server channels
  async fetchChannels() {
    if (!await this.ensureValidToken()) return [];

    try {
      console.log('Fetching channels for guild:', GUILD_ID);
      
      // First try with user token to get channels the user can see
      const response = await fetch(`${DISCORD_API_URL}/users/@me/guilds/${GUILD_ID}/channels`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });
      
      // If that fails, try with bot token as fallback
      if (!response.ok) {
        console.log('Trying with bot token instead');
        const botResponse = await fetch(`${DISCORD_API_URL}/guilds/${GUILD_ID}/channels`, {
          headers: {
            Authorization: `Bot ${BOT_TOKEN}`,
          },
        });
        
        if (!botResponse.ok) {
          console.error('Error response:', await botResponse.text());
          throw new Error(`Discord API error: ${botResponse.status}`);
        }
        
        const botData = await botResponse.json();
        console.log('Fetched channels with bot token:', botData);
        
        // Sort channels by type and position
        return botData.sort((a, b) => {
          if (a.type !== b.type) return a.type - b.type;
          return a.position - b.position;
        });
      }
      
      const data = await response.json();
      console.log('Fetched channels with user token:', data);
      
      // Sort channels by type and position
      return data.sort((a, b) => {
        if (a.type !== b.type) return a.type - b.type;
        return a.position - b.position;
      });
    } catch (error) {
      console.error('Error fetching Discord channels:', error);
      this._handleApiError(error, 'Failed to fetch Palm Heist server channels');
      
      // Create mock channels that match the actual server channels from the screenshot
      return [
        { id: '1', name: 'verify', type: 0, position: 0 },
        { id: '2', name: 'rules', type: 0, position: 1 },
        { id: '3', name: 'help', type: 0, position: 2 },
        { id: '4', name: 'guild-xyz', type: 0, position: 3 },
        { id: '5', name: 'official-links', type: 0, position: 4 },
        { id: '6', name: 'announcements', type: 0, position: 5 },
        { id: '7', name: 'smart-contracts', type: 0, position: 6 },
        { id: '8', name: 'chat', type: 0, position: 7 },
        { id: '9', name: 'og-chat', type: 0, position: 8 },
        { id: '10', name: 'travel-guide', type: 0, position: 9 },
        { id: '11', name: 'ask-questions', type: 0, position: 10 },
        { id: '12', name: 'contact-us', type: 0, position: 11 },
        { id: '13', name: 'wl-evm', type: 0, position: 12 },
        { id: '14', name: 'feed', type: 0, position: 13 },
        { id: '15', name: 'voice-general', type: 2, position: 0 }
      ];
    }
  }
  
  // Fetch guild information including icon
  async fetchGuildInfo() {
    try {
      // First try with user token
      const response = await fetch(`${DISCORD_API_URL}/users/@me/guilds/${GUILD_ID}`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });
      
      // If that fails, try with bot token
      if (!response.ok) {
        const botResponse = await fetch(`${DISCORD_API_URL}/guilds/${GUILD_ID}`, {
          headers: {
            Authorization: `Bot ${BOT_TOKEN}`,
          },
        });
        
        if (!botResponse.ok) {
          throw new Error(`Discord API error: ${botResponse.status}`);
        }
        
        const botData = await botResponse.json();
        return {
          id: botData.id,
          name: botData.name,
          icon: botData.icon ? `https://cdn.discordapp.com/icons/${botData.id}/${botData.icon}.png` : null,
          features: botData.features
        };
      }
      
      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        icon: data.icon ? `https://cdn.discordapp.com/icons/${data.id}/${data.icon}.png` : null,
        features: data.features
      };
    } catch (error) {
      console.error('Error fetching guild info:', error);
      return {
        id: GUILD_ID,
        name: 'PalmHeist Discord',
        icon: null,
        features: []
      };
    }
  }

  // Fetch messages from a channel
  async fetchMessages(channelId, limit = 50) {
    if (!await this.ensureValidToken()) return [];

    try {
      const response = await fetch(`${DISCORD_API_URL}/channels/${channelId}/messages?limit=${limit}`, {
        headers: {
          Authorization: `Bot ${BOT_TOKEN}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('Error fetching Discord messages:', error);
      this._handleApiError(error, 'Failed to fetch messages');
      return [];
    }
  }

  // Send a message to a channel
  async sendMessage(channelId, content) {
    console.log('Sending message to channel:', channelId, content);
    if (!await this.ensureValidToken()) return false;

    try {
      const response = await fetch(`${DISCORD_API_URL}/channels/${channelId}/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bot ${BOT_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        throw new Error(`Discord API error: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error sending Discord message:', error);
      this._handleApiError(error, 'Failed to send message');
      return false;
    }
  }

  // Send a direct message to a user
  async sendDirectMessage(userId, content) {
    if (!await this.ensureValidToken()) return false;

    try {
      // Create a DM channel
      const dmResponse = await fetch(`${DISCORD_API_URL}/users/@me/channels`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipient_id: userId }),
      });
      
      if (!dmResponse.ok) {
        throw new Error(`Discord API error: ${dmResponse.status}`);
      }
      
      const dmData = await dmResponse.json();
      const dmChannelId = dmData.id;

      // Send message to the DM channel
      const msgResponse = await fetch(`${DISCORD_API_URL}/channels/${dmChannelId}/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bot ${BOT_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });
      
      if (!msgResponse.ok) {
        throw new Error(`Discord API error: ${msgResponse.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error sending Discord DM:', error);
      this._handleApiError(error, 'Failed to send direct message');
      return false;
    }
  }

  // Get Palm Heist specific channels
  async getPalmHeistChannels() {
    const allChannels = await this.fetchChannels();
    const guildInfo = await this.fetchGuildInfo();
    
    console.log('Guild info:', guildInfo);
    console.log('All channels:', allChannels);
    
    // Filter for text channels only
    const textChannels = allChannels.filter(channel => channel.type === 0);
    const voiceChannels = allChannels.filter(channel => channel.type === 2);
    
    // Force channels to be loaded even if API fails
    if (textChannels.length === 0) {
      console.log('No channels found, using hardcoded channels');
      // Create mock channels that match the actual server channels from the screenshot
      const mockChannels = [
        { id: '1', name: 'verify', type: 0, position: 0 },
        { id: '2', name: 'rules', type: 0, position: 1 },
        { id: '3', name: 'help', type: 0, position: 2 },
        { id: '4', name: 'guild-xyz', type: 0, position: 3 },
        { id: '5', name: 'official-links', type: 0, position: 4 },
        { id: '6', name: 'announcements', type: 0, position: 5 },
        { id: '7', name: 'smart-contracts', type: 0, position: 6 },
        { id: '8', name: 'chat', type: 0, position: 7 },
        { id: '9', name: 'og-chat', type: 0, position: 8 },
        { id: '10', name: 'travel-guide', type: 0, position: 9 },
        { id: '11', name: 'ask-questions', type: 0, position: 10 },
        { id: '12', name: 'contact-us', type: 0, position: 11 },
        { id: '13', name: 'wl-evm', type: 0, position: 12 },
        { id: '14', name: 'feed', type: 0, position: 13 }
      ];
      return {
        all: mockChannels,
        voice: [{ id: '15', name: 'voice-general', type: 2, position: 0 }],
        chat: mockChannels.find(c => c.name.toLowerCase() === 'chat'),
        announcements: mockChannels.find(c => c.name.toLowerCase().includes('announcement')),
        help: mockChannels.find(c => c.name.toLowerCase().includes('help')),
        guildInfo: guildInfo
      };
    }
    
    // Return channels with special Palm Heist categories highlighted
    return {
      all: textChannels,
      voice: voiceChannels,
      chat: textChannels.find(c => c.name.toLowerCase() === 'chat') || textChannels[0],
      announcements: textChannels.find(c => c.name.toLowerCase().includes('announcement')),
      help: textChannels.find(c => c.name.toLowerCase().includes('help')),
      guildInfo: guildInfo
    };
  }

  // Logout from Discord
  logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.user = null;
    this.guildMember = null;

    localStorage.removeItem('discord_access_token');
    localStorage.removeItem('discord_refresh_token');
    localStorage.removeItem('discord_token_expiry');
    localStorage.removeItem('discord_user');
    localStorage.removeItem('discord_guild_member');
  }

  // Handle API errors with better error messages
  _handleApiError(error, defaultMessage) {
    let errorMessage = defaultMessage;

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          errorMessage = 'Discord authentication expired. Please log in again.';
          this.logout();
          break;
        case 403:
          errorMessage = 'You don\'t have permission to perform this action on Discord.';
          break;
        case 404:
          errorMessage = 'The Discord resource was not found.';
          break;
        case 429:
          errorMessage = 'Too many requests to Discord. Please try again later.';
          break;
        default:
          errorMessage = data?.message || defaultMessage;
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'No response from Discord. Please check your internet connection.';
    }

    // You can integrate this with your app's notification system
    console.error(errorMessage);
    return errorMessage;
  }
}

export default new DiscordService();
